<template>
  <!-- Renderless 组件：全局独立管理「持续跟踪 (RegionDirectoryTaskResult)」 图层 -->
  <teleport to="#home-body" v-if="regionDirectoryShowFlag && preAnnotationList && preAnnotationList.length > 0">
    <div class="tree-exposure-count-list">
      <div class="exposure-count-item exposure-count-title">
        <div>名录名称</div>
        <div>事件数</div>
      </div>
      <el-scrollbar max-height="20rem">
        <div class="exposure-count-item" v-for="(exposure, index) of preAnnotationList" :key="index"
          @click="preAnnotationListClick(exposure)">
          <div class="ellipsis">{{ (exposure['data'] || {})['annotationName'] || (exposure['data'] || {})['regionName'] }}</div>
          <div>{{ ((exposure['data'] || {})['eventList'] || []).length }}</div>
        </div>
      </el-scrollbar>
    </div>
  </teleport>

  <teleport to="#home-body">
    <EditPopup class="manage-panel-exposure-record-Charts" v-if="frequencyChart.data" :title="frequencyChart.title"
      :controls="frequencyChart.controls">
      <FrequencyChart :showFlag="true" :dataList="frequencyChart.data" ref="frequencyChartRef"></FrequencyChart>
    </EditPopup>
  </teleport>

  <teleport to="#home-body">
    <EventInfo v-if="eventTarget" :target="eventTarget" @close="eventTargetClose"></EventInfo>
  </teleport>
</template>

<script setup>
import {
  onMounted, onUnmounted,
  computed, ref, shallowRef, reactive, toRaw, watch,
} from 'vue';
import store from '@/store';
import { queryRegionDirectoryTaskResult } from '@/api/RegionDirectoryApi';
import PreAnnotationUse from '@/views/Home/component/ManagePanel/components/SelectedTreeTable/utils/PreAnnotationUse';
import { destroyAllPopups } from '@/views/Home/component/ManagePanel/components/SelectedTreeTable/utils/usePolyInfoPanel';
import FrequencyChart from '@/views/Home/component/ManagePanel/components/WorkSpace/components/WorkSpaceData/components/CustomLocaleControl/components/Frequency/components/FrequencyChart';
import EditPopup from '@/views/Home/component/ManagePanel/components/WorkSpace/components/WorkSpaceData/components/CustomLocaleControl/components/EditPopup';
import EventInfo from '@/views/Home/component/ManagePanel/components/SelectedTreeTable/components/EventInfo';

const xlsx = require('xlsx');

const homeMap = computed(() => store.getters['getHomeMap']),
  map = toRaw(homeMap.value),
  layerGroup = L.featureGroup().addTo(map);

// zoom 回调管理
const preAnnotationMapZoomMap = new Map();
const zoomCallBack = (lg, warningMarkerList, infoLayerGroup) => {
  const zoomHandler = (zoom) => {
    if (zoom >= 6) {
      warningMarkerList.forEach(marker => lg.removeLayer(marker));
      lg.addLayer(infoLayerGroup);
      return;
    }
    if (zoom <= 4) {
      warningMarkerList.forEach(marker => lg.addLayer(marker));
      lg.removeLayer(infoLayerGroup);
    }
  };
  preAnnotationMapZoomMap.set(lg, zoomHandler);
  lg.on({
    remove: () => preAnnotationMapZoomMap.delete(lg),
    add: () => preAnnotationMapZoomMap.set(lg, zoomHandler),
  });
};

const mapZoomendFn = (e) => {
  const zoom = e.target.getZoom();
  preAnnotationMapZoomMap.forEach((handler) => handler && handler(zoom));
};

// 事件/频次图表
const eventTarget = ref(undefined);
const eventTargetChange = (target) => { eventTarget.value = target; };
const eventTargetClose = () => { eventTarget.value = undefined; };

const frequencyChartRef = ref();
const chartDownloadFn = () => {
  frequencyChartRef.value?.chartDownloadFn?.('频次图表');
};

const downloadList = [
  { name: '日期', prop: 'exposureTime' },
  { name: '次数', prop: 'count' },
];
const excelHeader = downloadList.map(e => e.name);
const downloadExposureFn = () => {
  const data = [
    excelHeader,
    ...(frequencyChart.data.map(item => downloadList.map(e => item[e.prop])))
  ];
  const ws = xlsx.utils.aoa_to_sheet(data);
  const wb = xlsx.utils.book_new();
  xlsx.utils.book_append_sheet(wb, ws, "Sheet1");
  xlsx.writeFile(wb, 'download.xlsx');
};

const frequencyChartInitFn = (item) => {
  if ((item['exposureCountList'] || []).length == 0) return;
  frequencyChart.title = `${item['annotationName']}线索区频次`;
  frequencyChart.data = item['exposureCountList'];
};

const frequencyChart = reactive({
  title: '频次',
  data: undefined,
  controls: [
    { iconName: 'download-center', fill: 'rgb(102, 203, 118)', title: '数据导出', onClick: downloadExposureFn },
    { iconName: 'image', fill: 'rgb(102, 203, 118)', onClick: chartDownloadFn },
    { iconName: 'close-box', fill: 'rgb(6, 180, 221)', onClick: () => { frequencyChart.data = undefined } },
  ]
});

// Vuex 显隐状态监听
const globalRegionGroup = shallowRef(null);
const regionDirectoryShowFlag = computed(() => store.getters['regionDirectoryShowFlag']);

watch(regionDirectoryShowFlag, (newd) => {
  if (!globalRegionGroup.value) return;
  if (newd) {
    layerGroup.addLayer(globalRegionGroup.value);
  } else {
    layerGroup.removeLayer(globalRegionGroup.value);
    destroyAllPopups(); // 面板隐藏时彻底清理弹窗单例
  }
});

const preAnnotationList = computed(() => {
  if (!globalRegionGroup.value) return [];
  const dataLayerList = globalRegionGroup.value.options && globalRegionGroup.value.options['dataLayerList'];
  return dataLayerList || [];
});

const preAnnotationListClick = (target) => {
  const { layer } = target;
  const bounds = layer && layer.getBounds();
  bounds && bounds.isValid() && map.fitBounds(bounds, { maxZoom: 7 });
};

const queryRegionDirectoryTaskResultFn = () => {
  queryRegionDirectoryTaskResult({}).then(res => {
    if (res.data.code == 1004) {
      globalRegionGroup.value = PreAnnotationUse(
        res.data.data || [],
        frequencyChartInitFn,
        eventTargetChange,
        zoomCallBack,
        map,
      );
      if (regionDirectoryShowFlag.value) {
        layerGroup.addLayer(globalRegionGroup.value);
      }
    }
  }).catch(err => {
    console.log(err);
  });
};

onMounted(() => {
  map.on('zoomend', mapZoomendFn);
  queryRegionDirectoryTaskResultFn();
});

onUnmounted(() => {
  map.off('zoomend', mapZoomendFn);
  destroyAllPopups();
  if (globalRegionGroup.value) {
    layerGroup.removeLayer(globalRegionGroup.value);
  }
  map.removeLayer(layerGroup);
});
</script>

<style lang='less'>
.tree-exposure-count-list {
  position: absolute;
  bottom: 80px;
  right: 10px;
  border-radius: var(--borderRadiusSize_2);
  box-shadow: var(--boxShadow);
  background: var(--backgroundColor_2);
  border: 1px solid var(--borderColor_9);
  padding: 10px;
  backdrop-filter: blur(15px);
  width: 240px;
  max-height: 400px;

  .el-scrollbar {
    .el-scrollbar__view {
      display: flex;
      flex-direction: column;
      gap: 5px;
    }
  }

  .exposure-count-item {
    display: flex;
    cursor: pointer;
    border-radius: var(--borderRadiusSize);
    padding: 5px;

    &:hover {
      background-color: var(--backgroundColor_2);
    }

    div {
      font-size: 16px;
      font-family: OPPOSans-B;

      &:nth-child(1) {
        color: var(--fontColor_5);
        text-align: left;
        flex: 1;
        min-width: 0;
      }

      &:nth-child(2),
      &:nth-child(3) {
        color: var(--fontColor_12);
        width: 60px;
        text-align: center;
      }
    }
  }

  .exposure-count-title {
    &:hover {
      background-color: transparent;
    }

    div {
      font-size: 14px;
      font-family: OPPOSans;

      &:nth-child(1) {
        color: var(--fontColor_10);
        text-align: left;
        flex: 1;
        min-width: 0;
      }

      &:nth-child(2),
      &:nth-child(3) {
        color: var(--fontColor_10);
        text-align: center;
      }
    }
  }
}
</style>
